// Diet plan questions, opened from the path card: body → movement → diet goal → diets → allergies → plan.
// Required: weight, height and an age range (they move calories most). Sex, steps, sport, goal,
// diets and allergies are optional.
// Nothing is saved until the last step; skipping (any step) leaves no profile behind, and the
// fields start empty instead of assuming someone's measures.
import React, { useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useSettings } from '../store/settings';
import { AGE_RANGES, DEFAULT_STEPS, SPORTS, ALLERGEN_DIETS, ALLERGENS, LIMITS, SEXES, dietPlan, goalOf, parseMeasure, type AgeRange, type Allergen, type DietPlan, type Sex, type Sport } from '../logic/dietPlan';
import { PlanValues } from '../components/DietPlanCard';
import Icon from '../components/Icon';
import { Body, Button, Caption, Chip, Field, ProgressBar, Row } from '../components/ui';
import { DIETS, type Diet } from '../types';
import { font, radius, spacing, usePalette } from '../theme';

const STEPS = ['body', 'movement', 'goal', 'diets', 'allergies', 'plan'] as const;

export default function DietPlanScreen() {
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const insets = useSafeAreaInsets();
  const s = useSettings();
  const old = s.dietProfile;
  const [i, setI] = useState(0);
  const [weight, setWeight] = useState(old ? String(old.weightKg) : '');
  const [height, setHeight] = useState(old ? String(old.heightCm) : '');
  const [age, setAge] = useState<AgeRange | null>(old?.age ?? null);
  const [sex, setSex] = useState<Sex | undefined>(old?.sex);
  const [steps, setSteps] = useState(old?.steps !== undefined ? String(old.steps) : '');
  const [sports, setSports] = useState<Partial<Record<Sport, string>>>(
    Object.fromEntries(Object.entries(old?.sports ?? {}).map(([k, v]) => [k, String(v)])),
  );
  const [hasGoal, setHasGoal] = useState<boolean | null>(old ? goalOf(old) !== 'keep' : null);
  const [target, setTarget] = useState(old && goalOf(old) !== 'keep' ? String(old.targetWeightKg) : '');
  const [diets, setDiets] = useState<Diet[]>(s.diets);
  const [allergies, setAllergies] = useState<Allergen[]>(s.allergies);

  const weightKg = parseMeasure(weight, LIMITS.weightKg);
  const heightCm = parseMeasure(height, LIMITS.heightCm);
  const stepCount = parseMeasure(steps, LIMITS.steps);
  const sportMinutes = Object.fromEntries(
    SPORTS.map(x => [x, parseMeasure(sports[x] ?? '', LIMITS.sportMinutes)] as const).filter((e): e is [Sport, number] => !!e[1]),
  );
  const movementValid = (!steps.trim() || stepCount !== null) && SPORTS.every(x => !sports[x]?.trim() || parseMeasure(sports[x]!, LIMITS.sportMinutes) !== null);
  const targetKg = hasGoal ? parseMeasure(target, LIMITS.weightKg) : weightKg;
  const profile = weightKg && heightCm && age && targetKg ? { weightKg, heightCm, age, ...(sex ? { sex } : {}), ...(stepCount !== null ? { steps: Math.round(stepCount) } : {}), ...(Object.keys(sportMinutes).length ? { sports: sportMinutes } : {}), targetWeightKg: targetKg } : null;
  const plan = profile ? dietPlan(profile) : null;

  const step = STEPS[i];
  const canGoOn = step === 'body' ? !!(weightKg && heightCm && age) : step === 'movement' ? movementValid : step === 'goal' ? hasGoal === false || (hasGoal === true && !!targetKg) : true;
  const close = () => {
    if (nav.canGoBack()) nav.goBack();
    else nav.reset({ index: 0, routes: [{ name: 'Tabs' }] });
  };
  const save = () => {
    if (!profile) return;
    s.set({ dietProfile: profile, diets, allergies });
    close();
  };
  const toggle = <T,>(list: T[], x: T) => (list.includes(x) ? list.filter(y => y !== x) : [...list, x]);
  /** Shows why a filled-in value is not taken. */
  const invalid = (text: string, value: number | null, [min, max]: readonly [number, number]) =>
    text.trim() && value === null ? <Caption color={p.danger}>{t('dietPlan.range', { min, max })}</Caption> : null;

  return (
    <View style={{ flex: 1, backgroundColor: p.background, paddingTop: insets.top + spacing.l, paddingBottom: insets.bottom + spacing.l, gap: spacing.l }}>
      <Row style={{ flexWrap: 'nowrap', paddingHorizontal: spacing.xl }} gap={spacing.m}>
        <Text style={{ flex: 1, fontSize: font.title2, fontWeight: '800', color: p.text }}>{t('dietPlan.title')}</Text>
        {/* Skip: no profile is stored and the path card goes; Settings → Your diet plan redoes the questions. */}
        <Button
          label={old ? t('common.cancel') : t('dietPlan.skip')}
          variant="plain"
          onPress={() => {
            if (!old) s.set({ dietPlanSkipped: true });
            close();
          }}
        />
      </Row>
      <View style={{ paddingHorizontal: spacing.xl, gap: spacing.s }}>
        <ProgressBar value={(i + 1) / STEPS.length} color={p.tint} />
        <Caption>{t('onboarding.stepOf', { n: i + 1, total: STEPS.length })}</Caption>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: spacing.xl, gap: spacing.l, paddingBottom: spacing.l }} keyboardShouldPersistTaps="handled">
        <Text style={{ fontSize: font.title, fontWeight: '800', color: p.text }}>{t(`dietPlan.${step}Title`)}</Text>
        <Body secondary>{t(`dietPlan.${step}Body`)}</Body>

        {step === 'body' ? (
          <>
            <Field label={t('settings.weight')} keyboardType="decimal-pad" value={weight} onChangeText={setWeight} />
            {invalid(weight, weightKg, LIMITS.weightKg)}
            <Field label={t('settings.height')} keyboardType="decimal-pad" value={height} onChangeText={setHeight} />
            {invalid(height, heightCm, LIMITS.heightCm)}
            <Caption>{t('settings.age')}</Caption>
            <Row>
              {AGE_RANGES.map(r => (
                <Chip key={r} label={r.replace('-', '–')} selected={age === r} onPress={() => setAge(r)} />
              ))}
            </Row>
            <Caption>{t('dietPlan.sex')}</Caption>
            <Row>
              {/* Optional: tapping the chosen one again takes the answer back. */}
              {SEXES.map(x => (
                <Chip key={x} label={t(`dietPlan.sexes.${x}`)} selected={sex === x} onPress={() => setSex(sex === x ? undefined : x)} />
              ))}
            </Row>
            <Caption>{t('dietPlan.sexNote')}</Caption>
          </>
        ) : null}

        {step === 'movement' ? (
          <>
            <Field label={t('dietPlan.steps')} keyboardType="number-pad" value={steps} onChangeText={setSteps} />
            {invalid(steps, stepCount, LIMITS.steps)}
            <Caption>{t('dietPlan.stepsNote', { steps: DEFAULT_STEPS })}</Caption>
            <Text style={{ fontSize: font.headline, fontWeight: '700', color: p.text }}>{t('dietPlan.sport')}</Text>
            {SPORTS.map(x => (
              <View key={x} style={{ gap: 6 }}>
                <Field label={t(`dietPlan.sports.${x}`)} keyboardType="number-pad" value={sports[x] ?? ''} onChangeText={v => setSports(m => ({ ...m, [x]: v }))} />
                {invalid(sports[x] ?? '', sports[x]?.trim() ? parseMeasure(sports[x]!, LIMITS.sportMinutes) : null, LIMITS.sportMinutes)}
              </View>
            ))}
            <Caption>{t('dietPlan.sportNote')}</Caption>
          </>
        ) : null}

        {step === 'goal' ? (
          <>
            <Row style={{ flexWrap: 'nowrap' }} gap={spacing.m}>
              <Button label={t('common.yes')} variant={hasGoal === true ? 'primary' : 'secondary'} onPress={() => setHasGoal(true)} style={{ flex: 1 }} />
              <Button label={t('dietPlan.noGoal')} variant={hasGoal === false ? 'primary' : 'secondary'} onPress={() => setHasGoal(false)} style={{ flex: 1 }} />
            </Row>
            {hasGoal ? (
              <>
                <Field label={t('dietPlan.targetWeight')} keyboardType="decimal-pad" value={target} onChangeText={setTarget} autoFocus />
                {invalid(target, hasGoal ? targetKg : null, LIMITS.weightKg)}
              </>
            ) : null}
            {plan?.targetUnderweight ? <Warning text={t('dietPlan.underweight', { bmi: plan.targetBmi })} /> : null}
          </>
        ) : null}

        {step === 'diets' ? (
          <Row>
            {DIETS.map(d => (
              <Chip key={d} label={t(`diets.${d}`)} selected={diets.includes(d)} onPress={() => setDiets(x => toggle(x, d))} />
            ))}
          </Row>
        ) : null}

        {step === 'allergies' ? (
          <>
            <Row>
              {ALLERGENS.map(a => (
                <Chip key={a} label={t(`allergens.${a}`)} color={p.danger} selected={allergies.includes(a)} onPress={() => setAllergies(x => toggle(x, a))} />
              ))}
            </Row>
            {allergies.some(a => ALLERGEN_DIETS[a]) ? <Caption>{t('dietPlan.allergiesChecked')}</Caption> : null}
            {allergies.some(a => !ALLERGEN_DIETS[a]) ? <Warning text={t('dietPlan.allergiesUnchecked')} /> : null}
          </>
        ) : null}

        {step === 'plan' && plan ? (
          <>
            <View style={{ backgroundColor: p.card, borderRadius: radius.l * 1.5, padding: spacing.l, gap: spacing.s }}>
              <Text style={{ fontSize: font.title2, fontWeight: '800', color: p.text }}>{t('dietPlan.perDay', { kcal: plan.calories })}</Text>
              <Caption>
                {plan.goal === 'keep'
                  ? t('dietPlan.keep', { kcal: plan.maintenance })
                  : t(`dietPlan.pace_${plan.goal}`, { kg: Math.abs(plan.weeklyChangeKg), count: plan.weeks })}
              </Caption>
              <Caption>{t('dietPlan.bmi', { bmi: plan.bmi })}</Caption>
            </View>
            {plan.floored ? <Warning text={t('dietPlan.floored')} /> : null}
            {plan.targetUnderweight ? <Warning text={t('dietPlan.underweight', { bmi: plan.targetBmi })} /> : null}
            <EnergyParts plan={plan} />
            <PlanValues plan={plan} />
            <Caption>{t('dietPlan.disclaimer')}</Caption>
            <Button label={t('dietPlan.method')} icon="help" variant="plain" onPress={() => nav.navigate('Info', { page: 'dietPlanMethod' })} />
          </>
        ) : null}
      </ScrollView>

      <Row style={{ flexWrap: 'nowrap', paddingHorizontal: spacing.xl }} gap={spacing.m}>
        <Button label={t('onboarding.back')} variant="secondary" icon="back" disabled={i === 0} onPress={() => setI(x => x - 1)} style={{ flex: 1 }} iconOnly />
        {step === 'plan' ? (
          <Button label={t('dietPlan.save')} icon="check" onPress={save} disabled={!profile} style={{ flex: 1 }} />
        ) : (
          <Button label={t('onboarding.next')} icon="forward" disabled={!canGoOn} onPress={() => setI(x => x + 1)} style={{ flex: 1 }} iconOnly />
        )}
      </Row>
    </View>
  );
}

function Warning({ text }: { text: string }) {
  const p = usePalette();
  return (
    <Row gap={spacing.s} style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
      <Icon name="warning" size={18} color={p.warning} />
      <Caption style={{ flex: 1 }}>{text}</Caption>
    </Row>
  );
}

/** Where the maintenance calories come from: rest, then what each part adds. */
function EnergyParts({ plan }: { plan: DietPlan }) {
  const { t } = useTranslation();
  const p = usePalette();
  const e = plan.energy;
  const parts = [
    ['rest', e.rest],
    ['dailyLife', e.dailyLife],
    ['walking', e.walking],
    ['sport', e.sport],
    ['digestion', e.digestion],
  ] as const;
  return (
    <View style={{ gap: spacing.s }}>
      <Text style={{ fontSize: font.headline, fontWeight: '700', color: p.text }}>{t('dietPlan.energyTitle', { kcal: plan.maintenance })}</Text>
      <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
        {parts.map(([key, kcal]) => (
          <View key={key} style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: spacing.s, borderBottomWidth: 0.5, borderBottomColor: p.border, gap: spacing.s }}>
            <Text style={{ flex: 1, fontSize: font.body, color: p.text }}>{t(`dietPlan.energy.${key}`)}</Text>
            <Text style={{ fontSize: font.body, fontWeight: '700', color: p.text, fontVariant: ['tabular-nums'] }}>{key === 'rest' ? '' : '+ '}{kcal} kcal</Text>
          </View>
        ))}
      </View>
    </View>
  );
}
